import React, { useState } from "react";
import { Box, Text, IconButton, HStack, Tooltip } from "@chakra-ui/react";
import KnowledgeBaseArticle from "../../../models/KnowledgeBaseArticle";
import { useRecordFeedback } from "../../../networking/knowledgeBase.hooks";

interface ArticleViewerProps {
	article: KnowledgeBaseArticle;
	canEdit: boolean;
	onEdit: () => void;
}

/**
 * Component to display a knowledge base article with markdown rendering,
 * feedback buttons, and edit option for editors.
 */
const ArticleViewer: React.FC<ArticleViewerProps> = ({ article, canEdit, onEdit }) => {
	const [hasVoted, setHasVoted] = useState(false);
	const recordFeedback = useRecordFeedback();

	const handleFeedback = (isPositive: boolean) => {
		if (hasVoted) return;

		recordFeedback.mutate(
			{ articleId: article.id, isPositive },
			{
				onSuccess: () => {
					setHasVoted(true);
				},
			}
		);
	};

	// Simple markdown renderer (replace with react-markdown if needed)
	const renderMarkdown = (content: string) => {
		// Basic markdown rendering - split by lines and render headings, paragraphs, etc.
		const lines = content.split("\n");
		return lines.map((line, index) => {
			// Headings
			if (line.startsWith("### ")) {
				return (
					<Text key={index} fontSize="lg" fontWeight="semibold" mt={4} mb={2}>
						{line.substring(4)}
					</Text>
				);
			}
			if (line.startsWith("## ")) {
				return (
					<Text key={index} fontSize="xl" fontWeight="bold" mt={4} mb={2}>
						{line.substring(3)}
					</Text>
				);
			}
			if (line.startsWith("# ")) {
				return (
					<Text key={index} fontSize="2xl" fontWeight="bold" mt={4} mb={2}>
						{line.substring(2)}
					</Text>
				);
			}
			// Bold text
			const boldRegex = /\*\*(.*?)\*\*/g;
			const hasBold = boldRegex.test(line);
			if (hasBold) {
				const parts = line.split(boldRegex);
				return (
					<Text key={index} mb={2}>
						{parts.map((part, i) => (i % 2 === 1 ? <strong key={i}>{part}</strong> : part))}
					</Text>
				);
			}
			// Italic text
			const italicRegex = /\*(.*?)\*/g;
			const hasItalic = italicRegex.test(line);
			if (hasItalic) {
				const parts = line.split(italicRegex);
				return (
					<Text key={index} mb={2}>
						{parts.map((part, i) => (i % 2 === 1 ? <em key={i}>{part}</em> : part))}
					</Text>
				);
			}
			// Bullet points
			if (line.startsWith("- ") || line.startsWith("* ")) {
				return (
					<Text key={index} ml={4} mb={1}>
						• {line.substring(2)}
					</Text>
				);
			}
			// Empty lines
			if (line.trim() === "") {
				return <Box key={index} h={2} />;
			}
			// Regular paragraphs
			return (
				<Text key={index} mb={2}>
					{line}
				</Text>
			);
		});
	};

	return (
		<Box>
			{/* Article Header */}
			<HStack justify="space-between" mb={4}>
				<Text fontSize="2xl" fontWeight="bold">
					{article.title}
				</Text>
				{canEdit && (
					<IconButton
						aria-label="Edit article"
						icon={<Text>✏️</Text>}
						size="sm"
						variant="ghost"
						onClick={onEdit}
					/>
				)}
			</HStack>

			{/* Article Content */}
			<Box mb={6}>{renderMarkdown(article.content)}</Box>

			{/* Article Images */}
			{article.imageUrls && article.imageUrls.length > 0 && (
				<Box mb={6}>
					{article.imageUrls.map((url, index) => (
						<Box key={index} mb={4}>
							<img src={url} alt={`Article image ${index + 1}`} style={{ maxWidth: "100%" }} />
						</Box>
					))}
				</Box>
			)}

			{/* TODO: Disable this until we know if we want/need it */}
			{/* Feedback Section */}
			{/* <Box borderTop="1px solid" borderColor="gray.200" pt={4}>
				<Text fontSize="sm" color="gray.600" mb={2}>
					Hjälpte den här artikeln dig?
				</Text>
				<HStack spacing={2}>
					<Tooltip label={hasVoted ? "Tack för din feedback!" : "Hjälpsam"}>
						<IconButton
							aria-label="Thumbs up"
							icon={<Text>Ja</Text>}
							size="xs"
							variant={hasVoted ? "solid" : "outline"}
							colorScheme="green"
							onClick={() => handleFeedback(true)}
							isDisabled={hasVoted}
						/>
					</Tooltip>
					<Tooltip label={hasVoted ? "Tack för din feedback!" : "Ej hjälpsam"}>
						<IconButton
							aria-label="Thumbs down"
							icon={<Text>Nej</Text>}
							size="xs"
							variant={hasVoted ? "solid" : "outline"}
							colorScheme="red"
							onClick={() => handleFeedback(false)}
							isDisabled={hasVoted}
						/>
					</Tooltip>
					<Text fontSize="xs" color="gray.500" ml={4}>
						{article.feedbackStats.thumbsUp} helpful • {article.feedbackStats.thumbsDown} not
						helpful
					</Text>
				</HStack>
			</Box> */}
		</Box>
	);
};

export default ArticleViewer;
